(() => {
  if (customElements.get('cart-promo-carousel')) return;

  class CartPromoCarousel extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        if (this.isConnected) this.init();
      });
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      if (this._initialized) return;

      this.track = this.querySelector('.cart-promo-products__list');
      this.slides = Array.from(this.track?.querySelectorAll('.promo-product-card') || []);

      if (!this.track || this.dataset.stacked === 'true' || this.slides.length < 2) return;

      this._initialized = true;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.index = 0;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.interactionPaused = false;
      this.isVisible = false;
      this.interval = Math.max(2000, Number(this.dataset.autoplayInterval) || 2000);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.buildControls();

      this.slides.forEach((slide, index) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `${index + 1} מתוך ${this.slides.length}`);
      });

      this.previousButton.addEventListener('click', () => {
        this.goTo(this.index - 1);
        this.updateAutoplay();
      }, { signal });

      this.nextButton.addEventListener('click', () => {
        this.goTo(this.index + 1);
        this.updateAutoplay();
      }, { signal });

      /*
       * Autoplay follows interaction intent:
       * - Mouse hover anywhere in the promo wrapper pauses immediately.
       * - Keyboard focus anywhere inside the carousel pauses until focus leaves it.
       * - Touch / pointer dragging pauses for the duration of the gesture.
       * Every resume starts a fresh full interval, so a slide never moves immediately
       * after the user finishes reading or interacting.
       */
      this.addEventListener('mouseenter', () => {
        this.hoverPaused = true;
        this.updateAutoplay();
      }, { signal });

      this.addEventListener('mouseleave', () => {
        this.hoverPaused = false;
        this.updateAutoplay();
      }, { signal });

      this.addEventListener('focusin', () => {
        this.focusPaused = true;
        this.updateAutoplay();
      }, { signal });

      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

        this.focusPaused = false;
        this.updateAutoplay();
      }, { signal });

      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame) return;

        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.syncIndex();
        });
      }, { passive: true, signal });

      this.track.addEventListener('pointerdown', () => {
        this.interactionPaused = true;
        this.updateAutoplay();
      }, { passive: true, signal });

      window.addEventListener('pointerup', () => this.endInteraction(), { passive: true, signal });
      window.addEventListener('pointercancel', () => this.endInteraction(), { passive: true, signal });
      document.addEventListener('visibilitychange', () => this.updateAutoplay(), { signal });

      this.motionChangeHandler = () => this.updateAutoplay();
      this.motionQuery.addEventListener?.('change', this.motionChangeHandler);

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.75;
        this.updateAutoplay();
      }, { threshold: [0, 0.75, 1] });

      this.visibilityObserver.observe(this);

      requestAnimationFrame(() => this.goTo(0, 'auto'));
    }

    buildControls() {
      this.controls = this.querySelector('.cart-promo-products__controls');

      if (!this.controls) {
        this.controls = document.createElement('div');
        this.controls.className = 'cart-promo-products__controls';
        this.controls.setAttribute('role', 'group');
        this.controls.setAttribute('aria-label', 'ניווט בין המבצעים');

        this.controls.innerHTML = `
          <button type="button" class="cart-promo-products__control" data-promo-previous aria-label="המבצע הקודם">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg>
          </button>
          <button type="button" class="cart-promo-products__control" data-promo-next aria-label="המבצע הבא">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7"/></svg>
          </button>
        `;

        this.track.before(this.controls);
      }

      this.previousButton = this.controls.querySelector('[data-promo-previous]');
      this.nextButton = this.controls.querySelector('[data-promo-next]');
    }

    goTo(index, behavior = 'smooth') {
      if (!this.slides.length) return;

      this.index = (index + this.slides.length) % this.slides.length;

      this.slides[this.index].scrollIntoView({
        behavior: this.motionQuery.matches ? 'auto' : behavior,
        block: 'nearest',
        inline: 'start'
      });
    }

    syncIndex() {
      const trackRight = this.track.getBoundingClientRect().right;
      let nextIndex = 0;
      let smallestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().right - trackRight);

        if (distance < smallestDistance) {
          smallestDistance = distance;
          nextIndex = index;
        }
      });

      this.index = nextIndex;
    }

    endInteraction() {
      if (!this.interactionPaused) return;

      this.interactionPaused = false;
      this.updateAutoplay();
    }

    shouldAutoplay() {
      return (
        this.isVisible &&
        !document.hidden &&
        !this.hoverPaused &&
        !this.focusPaused &&
        !this.interactionPaused &&
        !this.motionQuery.matches
      );
    }

    updateAutoplay() {
      this.stopAutoplay();

      if (!this.shouldAutoplay()) return;

      this.timer = window.setTimeout(() => {
        this.goTo(this.index + 1);
        this.updateAutoplay();
      }, this.interval);
    }

    stopAutoplay() {
      if (!this.timer) return;

      window.clearTimeout(this.timer);
      this.timer = null;
    }

    destroy() {
      this.stopAutoplay();

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;

      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;

      if (this.motionQuery && this.motionChangeHandler) {
        this.motionQuery.removeEventListener?.('change', this.motionChangeHandler);
      }

      this.abortController?.abort();
      this.abortController = null;
      this._initialized = false;
    }
  }

  customElements.define('cart-promo-carousel', CartPromoCarousel);
})();
